/*
 * The campaign recipientsFilter, which is being introduced with
 * API Triggered Broadcasts, uses a new format that is different from
 * what's currently used in anchors/filters. It is no longer a base64-encoded
 * string and the underlying structure is also different.
 * These serializing functions transform the conditions format used by
 * anchors/filters into the new format.
 * For example:
 * [
 *   { negate: false, segments: [1], type: 'segments_negatable' },
 *   { negate: true, segments: [2], type: 'segments_negatable' }
 * ]
 * will be transformed into:
 * {
 *   and: [
 *     { segment: { id: 1 } },
 *     { not: { segment: { id: 2 } } }
 *   ]
 * }
 * Ideally this serializer won't be needed in it's current form,
 * but it's easier to do this rather than refactoring components like
 * condition-list to accept both formats.
 * In future, when we do abandon the old format, we might want to make this
 * into a similar structure like models/conditions and utils/conditions,
 * but that's overkill for now.
 */
import { isEmpty } from '@ember/utils';
import convertOperator from 'cio/utils/operators/convert';
import { MESSAGE_TYPE, TYPE_MAP } from 'cio/utils/named-bitmap-filters';
import { anyEmail } from 'cio/app-models/conditions/email';

function segmentConditionToFilter(condition) {
  let filter = {};

  let segments = condition.segments.map((id) => ({
    segment: { id },
  }));

  if (segments.length > 1) {
    filter = {
      or: segments,
    };
  } else {
    filter = segments[0];
  }

  if (condition.negate) {
    filter = {
      not: filter,
    };
  }

  return filter;
}

function attributeConditionToFilter(condition) {
  return attributeBasedConditionToFilter('attribute', condition);
}

function deviceAttributeConditionToFilter(condition) {
  return attributeBasedConditionToFilter('device_attribute', condition);
}

function attributeBasedConditionToFilter(name, condition) {
  let filter = {
    [name]: {
      field: condition.field,
    },
  };

  let { operator } = condition;
  let not = operator.indexOf('!') > -1;

  let convertedOperator = convertOperator(operator);

  if (not) {
    convertedOperator = convertedOperator.slice(1);
  }

  filter[name].operator = convertedOperator;

  if (condition.value) {
    filter[name].value = condition.value;
  }

  if (not) {
    filter = {
      not: filter,
    };
  }

  return filter;
}

// We need to convert our UI representation of conditions to what the backend
// expects for named_bitmap filters, which are of the format:
//
// {
//   "named_bitmap": {
//     "type": 3,
//     "name": "se", // this really is base64-encoded
//     "inverse": false
//   }
// }
function messageConditionToFilter(condition) {
  // eslint-disable-next-line prefer-destructuring
  let name = Object.entries(TYPE_MAP).find(([key, _value]) => key === condition.event.type)[1];

  if (condition.event.name !== anyEmail) {
    let underscoreIndex = condition.event.name.indexOf('_');
    if (underscoreIndex > -1) {
      let actionType = condition.event.name.substring(0, underscoreIndex);
      let actionId = condition.event.name.substring(underscoreIndex + 1, condition.event.name.length);

      if (actionId === '*') {
        name = `${name}-${actionType}`;
      } else {
        name = `${name}-${actionType}_${actionId}`;
      }
    }
  }

  let filter = {
    named_bitmap: {
      type: MESSAGE_TYPE,
      name: btoa(name), // the backend wants this base64 encoded
    },
  };

  if (condition.inverse) {
    filter = {
      not: filter,
    };
  }

  return filter;
}

function serializeCondition(condition) {
  switch (condition.type) {
    case 'segments_negatable':
      return segmentConditionToFilter(condition);
    case 'attribute':
      return attributeConditionToFilter(condition);
    case 'device_attribute':
      return deviceAttributeConditionToFilter(condition);
    case 'message':
      return messageConditionToFilter(condition);
  }
}

export default function serialize(conditions) {
  if (isEmpty(conditions)) {
    return {};
  }

  return {
    and: conditions.map((condition) => serializeCondition(condition)),
  };
}
